import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches a render error below it and shows `fallback` instead.
 *
 * Without one, any throw unmounted the whole app and left the page empty — in
 * the dark theme a black screen that only a reload got out of, with nothing
 * to say what had happened. The fallback gets the error, so the message can
 * be read off a screenshot, and `reset` to try again.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; fallback: (error: Error, reset: () => void) => ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Render error', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    return this.state.error ? this.props.fallback(this.state.error, this.reset) : this.props.children;
  }
}

/** The whole-app fallback: what broke, and a way back that keeps the address. */
export function AppCrash({ error }: { error: Error }) {
  return (
    <div className="mx-auto max-w-md px-5 py-16 text-center">
      <div className="text-4xl" aria-hidden>
        🧭
      </div>
      <h1 className="mt-4 text-xl font-semibold">Что-то сломалось на этой странице</h1>
      <p className="mt-2 text-sm text-[var(--text-muted)]">
        Изменения, которые уже сохранены, на месте. Перезагрузите страницу.
      </p>
      <pre className="mt-4 overflow-x-auto whitespace-pre-wrap rounded-md bg-[var(--surface-sunken)] p-3 text-left text-xs text-[var(--text-muted)]">
        {error.message}
      </pre>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-5 inline-flex h-11 items-center rounded-full bg-clay-500 px-6 text-sm font-semibold text-white"
      >
        Перезагрузить
      </button>
    </div>
  );
}
